import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";
import axios from "axios";
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import Alerts from "../../components/Alerts/alerts";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { useTranslation } from "react-i18next";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { uploadFile } from "../../utils/fileUtils";

const LedgerDetailTable = ({
  doctorId,
  status,
  updatedAt,
  rejectionReason,
  onClose,
  refreshList,
  doctorName,
  requestedAmount,
  withDrawlID
}) => {
  const { t } = useTranslation();
  const { authData } = PermissionAccess();
  const [data, setData] = useState([]);
  const [accountDetails, setAccountDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [pageLimit, setPageLimit] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const modalRef = useRef(null);
  const pdfContentRef = useRef(null);
  const [uploadedFilePath, setUploadedFilePath] = useState("");

  const [action, setAction] = useState("");
  const [reason, setReason] = useState(rejectionReason || "");

  const fetchDetails = () => {
    setLoading(true);

    DiscountCouponService.getWithDrawlRequestDetails(
      authData.sessionID,
      0,
      100,
      "",
      doctorId
    ).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          const cleanData = response.data.data.map(row => {
            const { atteched_file, ...rest } = row;
            return rest;
          });
          setData(cleanData);
          setTotalRows(response.data.count.count);
          setAccountDetails(response.data.accountDetails);
        } else {
          Alerts.swalErrorAlert(t("Unable to fetch details"));
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Server Error, Please try again later."));
      }
    );
  };

  useEffect(() => {
    if (status === "Rejected") {
      setAction("Rejected");
      setReason(rejectionReason);
    } else if (status === "Paid") {
      setAction("Approved");
    } else {
      setAction("");
    }
  }, [status, rejectionReason]);

  useEffect(() => {
    if (doctorId) fetchDetails();
  }, [doctorId, status, updatedAt]);

  const calculateTotals = () => {
    const credit = data
      .filter(row => row.ladger_action === '+')
      .reduce((sum, row) => sum + (row.doctor_actual_amount || 0), 0);
    
    // Only count actual debit transactions, exclude rejected withdrawal requests
    const debit = data
      .filter(row => row.ladger_action === '-' && row.status !== 'Rejected')
      .reduce((sum, row) => sum + (row.doctor_actual_amount || 0), 0);
    
    return { credit, debit, balance: credit - debit };
  };

  const totals = calculateTotals();

  const CustomPDFTable = ({ tableData }) => (
    <div style={{
      width: '100%',
      border: '2px solid #3ac47d',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        display: 'flex',
        backgroundColor: '#3ac47d',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        <div style={{ 
          width: '18%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'left'
        }}>
          {t("Date")}
        </div>
        <div style={{ 
          width: '52%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'left'
        }}>
          {t("Description")}
        </div>
        <div style={{ 
          width: '15%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'center'
        }}>
          {t("Credit (RM)")}
        </div>
        <div style={{ 
          width: '15%', 
          padding: '12px 8px',
          textAlign: 'center'
        }}>
          {t("Debit (RM)")}
        </div>
      </div>

      {tableData.map((row, index) => (
        <div key={index} style={{
          display: 'flex',
          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
          borderBottom: index < tableData.length - 1 ? '1px solid #e9ecef' : 'none',
          minHeight: '40px',
          alignItems: 'center'
        }}>
          <div style={{ 
            width: '18%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            fontSize: '12px',
            fontWeight: '500',
            color: '#495057'
          }}>
            {moment(row.datetime_added).format("DD MMM YYYY")}
          </div>
          <div style={{ 
            width: '52%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            fontSize: '12px',
            lineHeight: '1.3',
            color: '#495057'
          }}>
            {row.required_action}
          </div>
          <div style={{ 
            width: '15%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {row.ladger_action === '+' ? (
              <span style={{ color: '#3ac47d' }}>
                {row.doctor_actual_amount?.toFixed(2) || "0.00"}
              </span>
            ) : (
              <span style={{ color: '#6c757d' }}>-</span>
            )}
          </div>
          <div style={{ 
            width: '15%', 
            padding: '8px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {row.ladger_action === '-' && row.status !== 'Rejected' ? (
              <span style={{ color: '#dc3545' }}>
                {row.doctor_actual_amount?.toFixed(2) || "0.00"}
              </span>
            ) : row.ladger_action === '-' && row.status === 'Rejected' ? (
              <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                {t("Rejected")}
              </span>
            ) : (
              <span style={{ color: '#6c757d' }}>-</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const generatePDFWithWithdrawal = async () => {
    try {
      setPdfExporting(true);

      if (!data || data.length === 0) {
        console.warn("No data available for PDF generation.");
        setPdfExporting(false);
        throw new Error("No transaction data available.");
      }

      const withdrawalTransaction = {
        datetime_added: new Date().toISOString(),
        required_action: `Withdrawal Request - ${doctorName}`,
        ladger_action: '-',
        doctor_actual_amount: parseFloat(requestedAmount) || 0,
        status: 'Pending' // Add status to distinguish from rejected transactions
      };

      const originalData = [...data];
      setData([withdrawalTransaction, ...originalData]);

      await new Promise(resolve => setTimeout(resolve, 400));

      const modalElement = pdfContentRef.current;
      if (!modalElement) throw new Error("Content not found");

      const pdfContentWidth = 750;
      modalElement.style.width = `${pdfContentWidth}px`;
      modalElement.style.margin = '0 auto';

      await new Promise(resolve => setTimeout(resolve, 200));

      const canvas = await html2canvas(modalElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scrollY: -window.scrollY,
        width: pdfContentWidth,
        height: modalElement.scrollHeight + 100, // Add extra height
        windowWidth: pdfContentWidth,
        windowHeight: modalElement.scrollHeight + 100,
        x: 0,
        y: 0,
        logging: false
      });

      modalElement.style.width = '';
      modalElement.style.margin = '';

      setData(originalData);
      setPdfExporting(false);

      const imgData = canvas.toDataURL('image/png', 0.95);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const margin = 10;
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin);
      
      const scaleToFitWidth = availableWidth / (imgWidth * 0.264583);
      const scaleToFitHeight = availableHeight / (imgHeight * 0.264583);
      const finalScale = Math.min(scaleToFitWidth, scaleToFitHeight);
      
      const finalWidth = (imgWidth * 0.264583) * finalScale;
      const finalHeight = (imgHeight * 0.264583) * finalScale;
      
      const x = (pdfWidth - finalWidth) / 2;
      const y = margin;

      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);

      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      if (pdfBase64.length > 16000000) {
        throw new Error("PDF is too large to be stored in MongoDB (limit ~16MB).");
      }

      return pdfBase64;

    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfExporting(false);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!action) return Alerts.swalErrorAlert(t("Please select an action"));
    if (action === "Rejected" && !reason.trim()) {
      return Alerts.swalErrorAlert(t("Please enter rejection reason"));
    }

    setSubmitting(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        token: authData.sessionID
      };

      if (action === "Approved") {
        try {
          if (!uploadedFilePath) {
            return Alerts.swalErrorAlert(t("Please upload a PDF statement before proceeding."));
          }
          
          const payload = {
            functionName: "WithdrawlRequestAction",
            postData: {
              status: "Paid",
              rejection_reason: "",
              atteched_file: uploadedFilePath,
              doctor_id: doctorId,
              withdrawl_id: withDrawlID,
              "requested_amount": requestedAmount
            }
          };

          const response = await axios.post(`${process.env.REACT_APP_LOGINURL}LadgersModule/WithdrawlRequestAction`, payload, { headers });
          
          if (response?.data?.status && response?.data?.data?.acknowledged) {
            await fetchDetails();
            Alerts.swalSuccessAlert(t("Operation successful!"));
            onClose();
            refreshList();
          } else {
            Alerts.swalErrorAlert(response?.data?.message || t("Something went wrong"));
          }
        } catch (pdfError) {
          console.error("PDF generation failed:", pdfError);
          Alerts.swalErrorAlert(t("Failed to generate PDF. Please try again."));
        }
      } else {
        const payload = {
          functionName: "WithdrawlRequestAction",
          postData: {
            status: "Rejected",
            rejection_reason: reason,
            atteched_file: "",
            doctor_id: doctorId,
            withdrawl_id: withDrawlID,
            requested_amount: requestedAmount
          }
        };

        const response = await axios.post(`${process.env.REACT_APP_LOGINURL}LadgersModule/WithdrawlRequestAction`, payload, { headers });
        
        if (response?.data?.status && response?.data?.data?.acknowledged) {
          await fetchDetails();
          Alerts.swalSuccessAlert(t("Operation successful!"));
          onClose();
          refreshList();
        } else {
          Alerts.swalErrorAlert(t("Something went wrong"));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      Alerts.swalErrorAlert(t("Server Error, Please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      name: t("Date"),
      selector: row => moment(row.datetime_added).format("DD MMM YYYY"),
      width: "20%",
      sortable: true,
      style: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#495057'
      }
    },
    {
      name: t("Description"),
      selector: "required_action",
      width: "50%",
      sortable: true,
      wrap: true,
      style: { 
        fontSize: '14px',
        lineHeight: '1.4',
        color: '#495057'
      }
    },
    {
      name: t("Credit (RM)"),
      width: "15%",
      cell: row => (
        row.ladger_action === '+' ? 
        <span className="badge bg-success" style={{ fontSize: '12px' }}>
          {row.doctor_actual_amount?.toFixed(2) || "0.00"}
        </span> : 
        <span style={{ color: '#6c757d' }}>-</span>
      ),
      right: true,
      sortable: true,
    },
    {
      name: t("Debit (RM)"),
      width: "15%",
      cell: row => (
        row.ladger_action === '-' && row.status !== 'Rejected' ? 
        <span className="badge bg-danger" style={{ fontSize: '12px' }}>
          {row.doctor_actual_amount?.toFixed(2) || "0.00"}
        </span> : 
        row.ladger_action === '-' && row.status === 'Rejected' ?
        <span style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
          {t("Rejected")}
        </span> :
        <span style={{ color: '#6c757d' }}>-</span>
      ),
      right: true,
      sortable: true,
    }
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: '#ffffff',
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
      },
    },
    headRow: {
      style: {
        backgroundColor: '#3ac47d',
        borderBottom: '2px solid #2d9561',
        borderRadius: '12px 12px 0 0',
        minHeight: '52px'
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#ffffff',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        fontSize: '14px',
        borderBottom: '1px solid #f1f3f4',
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
        },
        '&:last-child': {
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
        }
      },
      stripedStyle: {
        backgroundColor: '#f8f9fa',
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '16px',
        paddingBottom: '16px',
      },
    },
  };

  return (
    <LoadingOverlay
      active={loading || submitting}
      spinner={<DNALoader />}
      styles={{ overlay: base => ({ ...base }) }}
      text={submitting ? t("Generating PDF...") : ""}
    >
      <div ref={modalRef} style={{ backgroundColor: 'white' }}>
        
        <div 
          ref={pdfContentRef} 
          style={{ 
            padding: pdfExporting ? '15px 15px 25px 15px' : '30px',
            backgroundColor: '#ffffff',
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
            width: pdfExporting ? '750px' : 'auto',
            maxWidth: pdfExporting ? '750px' : 'none',
            margin: pdfExporting ? '0 auto' : 'auto'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #3ac47d 0%, #2c5aa0 100%)',
            borderRadius: pdfExporting ? '8px' : '16px',
            padding: pdfExporting ? '20px' : '32px',
            marginBottom: pdfExporting ? '20px' : '32px',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '50%'
            }}></div>

            <div className="row align-items-start" style={{ position: 'relative', zIndex: 2 }}>
              <div className="col-md-8">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '6px',
                    height: '48px',
                    backgroundColor: '#ffd700',
                    borderRadius: '3px',
                    marginRight: '16px'
                  }}></div>
                  <div>
                    <h1 style={{ 
                      color: '#ffffff', 
                      fontWeight: '800',
                      fontSize: pdfExporting ? '24px' : '32px',
                      marginBottom: '4px',
                      letterSpacing: '-1px'
                    }}>
                      {t("Ledger Statement")}
                    </h1>
                    <p style={{ 
                      fontSize: pdfExporting ? '14px' : '18px',
                      fontWeight: '500',
                      marginBottom: '0',
                      opacity: '0.9'
                    }}>
                      {doctorName || t("Service Provider")}
                    </p>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  gap: pdfExporting ? '16px' : '24px', 
                  fontSize: pdfExporting ? '12px' : '14px', 
                  opacity: '0.9',
                  flexDirection: 'column',
                  marginTop: pdfExporting ? '12px' : '0'
                }}>
                  <div>
                    <strong>{t("Generated on")}:</strong><br />
                    {moment().format("DD MMMM YYYY, hh:mm A")}
                  </div>
                  <div>
                    <strong>{t("Statement Period")}:</strong><br />
                    {data.length > 0 ? 
                      `${moment(data[data.length-1]?.datetime_added).format("DD MMM YYYY")} - ${moment(data[0]?.datetime_added).format("DD MMM YYYY")}` 
                      : 'N/A'
                    }
                  </div>
                </div>
              </div>
              

              <div className="col-md-4">
  {status === "Paid" ? (
    <div style={{
      background: 'rgba(255,255,255,0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      padding: '16px',
      marginTop: '8px',
      border: '1px solid rgba(255,255,255,0.2)',
      color: '#ffffff'
    }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        {t("Summary")}
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: '13px',
        fontWeight: '500'
      }}>
        <div>
          {t("Total Credit")}: <strong style={{ color: '#fff' }}>RM {totals.credit.toFixed(2)}</strong>
        </div>
        <div>
          {t("Total Debit")}: <strong style={{ color: '#fff' }}>RM {totals.debit.toFixed(2)}</strong>
        </div>
        <div>
          {t("Net Balance")}: <strong style={{ color: totals.balance >= 0 ? '#3ac47d' : '#dc3545' }}>
            RM {totals.balance.toFixed(2)}
          </strong>
        </div>
      </div>
    </div>
  ) : (
    <div style={{
      background: 'rgba(255,255,255,0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      padding: '12px',
      marginTop: '8px',
      border: '1px solid rgba(255,255,255,0.2)',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        opacity: '0.9'
      }}>
        {t("Requested Amount")}
      </div>
      <div style={{ 
        fontSize: pdfExporting ? '18px' : '28px',
        fontWeight: '800',
        color: '#ffffff',
        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
      }}>
        RM {requestedAmount || '0.00'}
      </div>
    </div>
  )}
</div>


            </div>
          </div>

          {pdfExporting ? (
            <CustomPDFTable tableData={data} />
          ) : (
            <div style={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <DataTable
                data={data}
                columns={columns}
                pagination={false}
                fixedHeader
                fixedHeaderScrollHeight="300px"
                customStyles={customStyles}
                striped
                highlightOnHover
                responsive
                noDataComponent={
                  <div className="text-center py-5">
                    <div style={{
                      color: '#6c757d',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      <i className="fas fa-inbox me-2"></i>
                      {t("No transactions found")}
                    </div>
                  </div>
                }
              />
            </div>
          )}

          {data.length > 0 && status !== "Paid" && (
  <div style={{ marginTop: pdfExporting ? '20px' : '40px' }}>
    <div className="row justify-content-end">
      <div className="col-md-5">
        <div style={{ 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '16px',
          padding: '28px',
          border: '1px solid #dee2e6',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>
          <h6 style={{ 
            color: '#3ac47d',
            fontWeight: '800',
            letterSpacing: '1px',
            fontSize: pdfExporting ? '14px' : '16px',
            marginBottom: pdfExporting ? '16px' : '24px',
            textTransform: 'uppercase',
            borderBottom: '2px solid #3ac47d',
            paddingBottom: '8px'
          }}>
            {t("Summary")}
          </h6>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '12px 0',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#495057' }}>
              {t("Total Credit")}:
            </span>
            <span style={{ 
              fontSize: pdfExporting ? '14px' : '16px',
              fontWeight: '700',
              color: '#3ac47d',
              background: 'rgba(58, 196, 125, 0.1)',
              padding: '4px 12px',
              borderRadius: '20px',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              RM {totals.credit.toFixed(2)}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            padding: '12px 0',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#495057' }}>
              {t("Total Debit")}:
            </span>
            <span style={{ 
              fontSize: pdfExporting ? '14px' : '16px',
              fontWeight: '700',
              color: '#dc3545',
              background: 'rgba(220, 53, 69, 0.1)',
              padding: '4px 12px',
              borderRadius: '20px',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              RM {totals.debit.toFixed(2)}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 0',
            borderTop: '2px solid #3ac47d',
            marginTop: '16px'
          }}>
            <strong style={{ fontSize: pdfExporting ? '16px' : '18px', color: '#3ac47d', fontWeight: '800' }}>
              {t("Net Balance")}:
            </strong>
            <strong style={{ 
              fontSize: pdfExporting ? '18px' : '20px',
              fontWeight: '800',
              color: totals.balance >= 0 ? '#3ac47d' : '#dc3545',
              background: totals.balance >= 0 ? 'rgba(58, 196, 125, 0.15)' : 'rgba(220, 53, 69, 0.15)',
              padding: '8px 16px',
              borderRadius: '25px',
              border: `2px solid ${totals.balance >= 0 ? '#3ac47d' : '#dc3545'}`,
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              RM {totals.balance.toFixed(2)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  </div>
)}



          {!pdfExporting && (status === "Paid" || status === "Rejected") && (
            <div style={{ 
              marginTop: '32px',
              padding: '24px',
              borderRadius: '16px',
              background: status === "Paid" 
                ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)' 
                : 'linear-gradient(135deg, #f8d7da 0%, #f1b0b7 100%)',
              border: `2px solid ${status === "Paid" ? '#28a745' : '#dc3545'}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
            }}>
              <div className="text-center">
                <div style={{ marginBottom: '16px' }}>
                  <i 
                    className={`fas ${status === "Paid" ? 'fa-check-circle' : 'fa-times-circle'}`} 
                    style={{ 
                      fontSize: '32px',
                      color: status === "Paid" ? '#28a745' : '#dc3545',
                      marginRight: '12px'
                    }}
                  ></i>
                  <strong style={{ 
                    color: status === "Paid" ? '#155724' : '#721c24',
                    fontSize: '20px',
                    fontWeight: '700'
                  }}>
                    {status === "Paid" ? t("Payment Processed") : t("Request Rejected")}
                  </strong>
                </div>
                <div style={{ 
                  color: status === "Paid" ? '#155724' : '#721c24',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  {moment(updatedAt).format("DD MMMM YYYY, hh:mm A")}
                </div>
                {status === "Rejected" && rejectionReason && (
                  <div style={{ 
                    marginTop: '16px',
                    padding: '16px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}>
                    <strong>{t("Reason")}:</strong> {rejectionReason}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {(status !== "Paid") && (
          <div style={{ 
            padding: '32px', 
            borderTop: '3px solid #e9ecef', 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
          }}>
            <div className="d-flex justify-content-center">
              <div style={{ width: "100%", maxWidth: "700px" }}>
                <div className="form-group mb-4">
                  <label className="form-label fw-bold mb-3" style={{ 
                    fontSize: '18px',
                    color: '#3ac47d',
                    display: 'block'
                  }}>
                    {t("Select Action")}
                  </label>
                  <select 
                    className="form-select form-select-lg shadow-sm" 
                    value={action} 
                    onChange={(e) => setAction(e.target.value)}
                    style={{ 
                      borderColor: '#ced4da',
                      fontSize: '16px',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      borderWidth: '2px'
                    }}
                  >
                    <option value="">{t("Select Action")}</option>
                    <option value="Approved">{t("Approve & Process Payment")}</option>
                    <option value="Rejected">{t("Reject Request")}</option>
                  </select>
                </div>

                {action === "Rejected" && (
                  <div className="form-group mb-4">
                    <label className="form-label fw-bold mb-3" style={{ 
                      fontSize: '18px',
                      color: '#dc3545',
                      display: 'block'
                    }}>
                      {t("Reason for Rejection")}
                    </label>
                    <textarea
                      className="form-control shadow-sm"
                      rows={4}
                      placeholder={t("Please provide a detailed reason for rejection...")}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      style={{ 
                        borderColor: '#ced4da',
                        fontSize: '15px',
                        borderRadius: '12px',
                        padding: '16px',
                        borderWidth: '2px',
                        resize: 'vertical'
                      }}
                    ></textarea>
                  </div>
                )}

                {action === "Approved" && (
                <div className="form-group mb-4">
                  <label className="form-label fw-bold mb-2" style={{ 
                    fontSize: '16px', color: '#3ac47d' 
                  }}>
                    {t("Upload PDF Statement")}
                  </label>
                  <input 
                    type="file"
                    accept="application/pdf"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) {
                        return;
                      }
                      try {
                        const path = await uploadFile(file);
                        setUploadedFilePath(path);
                      } catch (error) {
                        Alerts.swalErrorAlert(error.message || t("PDF upload failed."));
                      }
                    }}
                    className="form-control"
                    style={{ borderRadius: '12px', padding: '10px' }}
                  />
                </div>
              )}

              


                {(action === "Approved" || action === "Rejected") && (
                  <div className="d-flex justify-content-center gap-4 mt-5">
                   
                    <button
                      className={`btn btn-lg px-5 shadow-sm ${action === "Approved" ? 'btn-success' : 'btn-danger'}`}
                      onClick={handleSubmit}
                      disabled={submitting}
                      style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '12px',
                        padding: '12px 32px',
                        borderWidth: '0',
                        background: action === "Approved" 
                          ? 'linear-gradient(135deg, #3ac47d 0%, #2d9561 100%)'
                          : 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)'
                      }}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          {t("Processing...")}
                        </>
                      ) : (
                        <>
                          <i className={`fas ${action === "Approved" ? 'fa-check' : 'fa-ban'} me-2`}></i>
                          {action === "Approved" ? t("Approve & Process") : t("Reject Request")}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

<div className="form-group mb-4 mt-4">
  <label 
    className="form-label fw-bold mb-3" 
    style={{
      fontSize: '20px',
      color: '#2c5aa0',
      fontWeight: '700',
      display: 'block',
      borderBottom: '2px solid #2c5aa0',
      paddingBottom: '10px',
      marginBottom: '15px'
    }}
  >
    {t("Service Provider Bank Account Details")}
  </label>

  <div style={{
    backgroundColor: '#ffffff',
    border: '1px solid #e2e2e2',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px'
  }}>
    <div style={{ marginBottom: '15px', fontSize: '16px', color: '#495057' }}>
      <strong>{t("Account Holder Name")}:</strong> {accountDetails?.account_name || "-"}
    </div>

    <div style={{ marginBottom: '15px', fontSize: '16px', color: '#495057' }}>
      <strong>{t("Account No")}:</strong> {accountDetails?.bank_account_number || "-"}
    </div>

    <div style={{ marginBottom: '15px', fontSize: '16px', color: '#495057' }}>
      <strong>{t("Bank")}:</strong> {accountDetails?.bank_details?.name || "-"}
    </div>

  </div>
</div>


        
        

      </div>
    </LoadingOverlay>
  );
};

export default LedgerDetailTable;